/**
 * Presentation contract published by each game module.
 *
 * A skin must satisfy its game's contract before it can be published. The
 * contract describes geometry, symbol roles and states, events the renderer
 * must handle, counters, layout elements, animation hooks and audio cues. It
 * contains no probabilities, payouts or rules: those live in the game module.
 */

export const DESIGN_SIZE = {
  landscape: { w: 1920, h: 1080 },
  portrait: { w: 1080, h: 1920 },
} as const;
export type Orientation = keyof typeof DESIGN_SIZE;
export const SAFE_INSET = 32;

export type SymbolRole =
  | 'low'
  | 'high'
  | 'wild'
  | 'scatter'
  | 'multiplier'
  | 'value'
  | 'collector'
  | 'booster'
  | 'unlocker'
  | 'timer'
  | 'activator'
  | 'blank'
  | 'special';

export type SymbolState = 'idle' | 'land' | 'win' | 'removal' | 'feature';

export const ANIM_PRESETS = [
  'none',
  'pulse',
  'glow',
  'shake',
  'pop',
  'bounce',
  'spin',
  'shatter',
  'dissolve',
  'fade',
  'float',
  'flash',
  'zoom',
  'drop',
  'swing',
] as const;
export type AnimPreset = (typeof ANIM_PRESETS)[number];

export const EASINGS = ['linear', 'easeIn', 'easeOut', 'easeInOut', 'backOut', 'elasticOut', 'bounceOut'] as const;
export type Easing = (typeof EASINGS)[number];

export const PARTICLE_PRESETS = [
  'none',
  'sparks',
  'embers',
  'bubbles',
  'petals',
  'stars',
  'confetti',
  'smoke',
  'shards',
  'spores',
  'lightning',
  'coins',
] as const;
export type ParticlePreset = (typeof PARTICLE_PRESETS)[number];

/** Animation bounds enforced by schema AND at runtime. */
export const ANIM_LIMITS = { minMs: 0, maxMs: 4000, maxIntensity: 2 } as const;

export interface AnimBindingDefault {
  preset: AnimPreset;
  durationMs: number;
  easing: Easing;
  intensity?: number;
  particles?: ParticlePreset;
}

export interface LayoutElementSpec {
  id: string;
  kind: 'board' | 'image' | 'panel' | 'decor';
  required: boolean;
  /** Whether owners may move/resize it in the editor. */
  editable: boolean;
  /** Bounds in design units. */
  min: { w: number; h: number };
  max: { w: number; h: number };
  /** Width/height ratio to preserve when resizing, if any. */
  lockAspect?: number;
  /** For panels: counter ids shown in this panel. */
  counters?: string[];
  /** For image/decor elements: the asset slot rendered in it. */
  assetSlot?: string;
  /** Feature phases in which this element is visible (all when absent). */
  phases?: string[];
  description: string;
}

export interface CounterSpec {
  id: string;
  /** Default label key in skin.labels; e.g. "counter.freeSpins". */
  labelKey: string;
  /** credits: the value is x100 of the bet and is shown as a credit amount at the current stake. */
  format: 'int' | 'x' | 'x100' | 'progress' | 'credits';
  /** For 'progress': maximum value. */
  max?: number;
  phases?: string[];
}

export interface PresentationContract {
  id: string; // `${gameId}@${major}`
  gameId: string;
  version: string;
  eventSchemaVersion: string;
  board: {
    kind: 'grid' | 'reels';
    cols: number;
    rows: number;
    /** Maximum rows reachable through features (expanding play area). */
    maxRows?: number;
    evaluation: 'count' | 'cluster' | 'lines' | 'ways';
  };
  symbols: {
    id: string;
    role: SymbolRole;
    requiredStates: SymbolState[];
    /** Renderer draws the cell's value (multiplier/value) over this symbol. */
    valueBearing?: boolean;
    description: string;
  }[];
  eventTypes: string[];
  counters: CounterSpec[];
  featureStates: string[];
  elements: LayoutElementSpec[];
  animationHooks: { id: string; description: string; default: AnimBindingDefault }[];
  audioCues: { id: string; required: boolean; loop?: boolean; description: string }[];
  /** Asset slots that must be filled (besides symbols and audio). */
  requiredAssetSlots: string[];
  optionalAssetSlots: string[];
  /** Preview fixture ids shipped for this contract. */
  fixtures: string[];
  /** Label keys the skin may override (display text only). */
  labelKeys: string[];
}

/** Elements/slots/cues every slot contract shares. */
export const COMMON_ASSET_SLOTS = ['lobby.card', 'lobby.loading', 'logo', 'bg.base'] as const;
/**
 * The side character as short clips with a transparent background, in place of the still `character` (which stays the
 * fallback): `idle` loops, `run` plays while the reels spin, `win` / `lose` react to the round. Every clip starts and
 * ends on the same rest pose, so they cut into each other. Each ships as WebM (VP9 + alpha: Chrome, Edge, Firefox) in
 * `character.<clip>` and as HEVC + alpha (Safari, iPhone) in `character.<clip>.hevc`.
 */
export const CHARACTER_CLIPS = ['idle', 'run', 'win', 'lose'] as const;
export type CharacterClip = (typeof CHARACTER_CLIPS)[number];
export const CHARACTER_CLIP_SLOTS = ['character.idle', 'character.run', 'character.win', 'character.lose', 'character.idle.hevc', 'character.run.hevc', 'character.win.hevc', 'character.lose.hevc'] as const;
export const COMMON_OPTIONAL_SLOTS = ['bg.feature', 'frame', 'character', 'decor.1', 'decor.2', 'decor.3', 'decor.4', 'lobby.motion', 'bg.base.motion', 'bg.feature.motion', 'panel.plate', 'fx.feature.intro', 'fx.plaque', 'fx.win.1', 'fx.win.2', 'fx.win.3', 'fx.win.4', 'cabinet.idle', 'cabinet.celebrate', 'cabinet.header', 'hud.peek', ...CHARACTER_CLIP_SLOTS] as const;
/** Slots that take a muted video (MP4/H.264 loops; character clips WebM or HEVC). Decoration only: the still slot is always the fallback. */
export const VIDEO_SLOTS: readonly string[] = ['lobby.motion', 'bg.base.motion', 'bg.feature.motion', 'fx.feature.intro', 'cabinet.idle', 'cabinet.celebrate', ...CHARACTER_CLIP_SLOTS, 'drive.swing', 'drive.swing.hevc', 'drive.course.motion', 'drive.outcome.1', 'drive.outcome.2', 'drive.outcome.3', 'drive.outcome.4', 'drive.outcome.5', 'drive.outcome.miss'];
export const COMMON_AUDIO_CUES = [
  { id: 'music.base', required: true, loop: true, description: 'Base-game music/ambience loop' },
  { id: 'music.feature', required: true, loop: true, description: 'Feature music loop' },
  { id: 'ui.click', required: true, description: 'Button press' },
  { id: 'spin.start', required: true, description: 'Spin begins' },
  { id: 'spin.stop', required: true, description: 'Reel/board lands' },
  { id: 'win.small', required: true, description: 'Win below big-win tier' },
  { id: 'win.big', required: true, description: 'Big win celebration' },
  { id: 'feature.trigger', required: true, description: 'Feature triggered' },
  { id: 'feature.end', required: true, description: 'Feature summary' },
  { id: 'anticipation', required: false, description: 'Anticipation before a possible trigger' },
  { id: 'win.rollup', required: false, loop: true, description: 'Coin roll-up loop under a counting win amount (synthesized ticks when absent)' },
  { id: 'win.rollup.end', required: false, description: 'Ding when a win count-up lands (synthesized when absent)' },
  { id: 'fx.swoosh', required: false, description: 'Full-screen cinematic swipe / transition whoosh' },
  { id: 'fx.impact', required: false, description: 'Hero strike / impact in the feature intro clip' },
  { id: 'fx.fanfare', required: false, description: 'Award screen fanfare (free spins won, feature total)' },
] as const;

export const COMMON_HOOKS = [
  { id: 'spinStart', description: 'Board/reels start moving', default: { preset: 'drop', durationMs: 350, easing: 'easeIn' } },
  { id: 'symbolLand', description: 'Symbols land after spin or refill', default: { preset: 'bounce', durationMs: 260, easing: 'bounceOut' } },
  { id: 'winHighlight', description: 'Winning symbols highlight', default: { preset: 'pulse', durationMs: 700, easing: 'easeInOut', particles: 'sparks' } },
  { id: 'featureIntro', description: 'Transition into a feature', default: { preset: 'zoom', durationMs: 1400, easing: 'backOut', particles: 'stars' } },
  { id: 'featureOutro', description: 'Feature summary and return', default: { preset: 'fade', durationMs: 1600, easing: 'easeOut', particles: 'confetti' } },
  { id: 'bigWin', description: 'Big/mega/epic win overlay', default: { preset: 'zoom', durationMs: 2400, easing: 'elasticOut', particles: 'coins' } },
] as const satisfies readonly { id: string; description: string; default: AnimBindingDefault }[];

/**
 * Win tiers are fixed by the platform (not skinnable) so celebration intensity
 * always matches the real payout. Thresholds are x100 multiples of stake.
 */
export const WIN_TIERS = [
  { id: 'epic', minX100: 25_000 },
  { id: 'mega', minX100: 5_000 },
  { id: 'big', minX100: 1_500 },
  { id: 'nice', minX100: 500 },
  { id: 'small', minX100: 1 },
] as const;
export type WinTier = (typeof WIN_TIERS)[number]['id'] | 'none';

export function winTier(x100: number): WinTier {
  for (const t of WIN_TIERS) if (x100 >= t.minX100) return t.id;
  return 'none';
}

/** Standard layout elements shared by slot contracts. Games add panels/elements. */
export function standardElements(boardAspect: number, extra: LayoutElementSpec[] = []): LayoutElementSpec[] {
  return [
    {
      id: 'board',
      kind: 'board',
      required: true,
      editable: true,
      min: { w: 520, h: 360 },
      max: { w: 1500, h: 1400 },
      lockAspect: boardAspect,
      description: 'Game board. Logical size is fixed by the rules; only screen position/size can change.',
    },
    {
      id: 'frame',
      kind: 'image',
      required: false,
      editable: true,
      min: { w: 500, h: 340 },
      max: { w: 1920, h: 1920 },
      assetSlot: 'frame',
      description: 'Decorative frame drawn behind the board.',
    },
    {
      id: 'logo',
      kind: 'image',
      required: true,
      editable: true,
      min: { w: 160, h: 60 },
      max: { w: 900, h: 420 },
      assetSlot: 'logo',
      description: 'Game logo.',
    },
    {
      id: 'character',
      kind: 'image',
      required: false,
      editable: true,
      min: { w: 120, h: 120 },
      max: { w: 900, h: 1100 },
      assetSlot: 'character',
      description: 'Optional decorative character/illustration.',
    },
    ...[1, 2, 3, 4].map(
      (n): LayoutElementSpec => ({
        id: `decor.${n}`,
        kind: 'decor',
        required: false,
        editable: true,
        min: { w: 40, h: 40 },
        max: { w: 1920, h: 1920 },
        assetSlot: `decor.${n}`,
        description: `Optional decorative image ${n}.`,
      }),
    ),
    ...extra,
  ];
}
